import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import BrokerDashboard from "./pages/BrokerDashboard";
import AdminPage from "./pages/AdminPage";
import PublicLoanForm from "./pages/PublicLoanForm";
import PDFPreviewPage from "./pages/PDFPreviewPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Protected Route for Brokers
const BrokerRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Se verifică autentificarea...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect admins to admin panel
  if (user?.role === 'admin' || user?.role === 'subadmin') {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Protected Route for Admins/SubAdmins
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Se verifică autentificarea...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Only allow admin and subadmin roles
  if (user?.role !== 'admin' && user?.role !== 'subadmin') {
    return <Navigate to="/broker" replace />;
  }
  
  return children;
};

// Public Route Component (redirects authenticated users based on role)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Se verifică autentificarea...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect based on role
    if (user?.role === 'admin' || user?.role === 'subadmin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/broker" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* PDF Preview */}
      <Route path="/pdf-preview" element={<PDFPreviewPage />} />
      
      {/* Public Form Routes */}
      <Route path="/apply/:slug" element={<PublicLoanForm />} />
      <Route path="/apply/:slug/semnaturi" element={<PublicLoanForm />} />
      <Route path="/apply/invite/:inviteId" element={<PublicLoanForm />} />
      <Route path="/apply/codebitor/:codebitorLinkId" element={<PublicLoanForm />} />
      
      {/* Auth Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      
      {/* Broker Dashboard */}
      <Route 
        path="/broker" 
        element={
          <BrokerRoute>
            <BrokerDashboard />
          </BrokerRoute>
        } 
      />
      <Route 
        path="/broker/*" 
        element={
          <BrokerRoute>
            <BrokerDashboard />
          </BrokerRoute>
        } 
      />
      
      {/* Admin Panel */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } 
      />
      
      {/* Legacy routes - redirect */}
      <Route path="/dashboard" element={<Navigate to="/broker" replace />} />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary
            message="Pagina nu a putut fi afișată din cauza unei erori. Reîncărcați pagina pentru a continua."
            onReset={() => window.location.reload()}
          >
            <AppRoutes />
          </ErrorBoundary>
          <Toaster />
          <SonnerToaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
